import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getSeoData as getStaticSeoData } from '@/data/seoTranslations';

interface SeoData {
  title: string;
  description: string;
}

interface CacheEntry {
  data: SeoData;
  timestamp: number;
}

// Cache for public SEO data with TTL
const seoCache = new Map<string, CacheEntry>();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

/**
 * Check if cache entry is still valid
 */
function isCacheValid(entry: CacheEntry | undefined): entry is CacheEntry {
  if (!entry) return false;
  return Date.now() - entry.timestamp < CACHE_TTL;
}

/**
 * Hook to get SEO data with database priority (for public pages)
 * Falls back to static translations if database entry doesn't exist
 * Uses 30-minute cache TTL for better performance
 */
export function useDynamicSeoData(page: string, language: string) {
  const [seoData, setSeoData] = useState<SeoData>(() => {
    const cacheKey = `${page}_${language}`;
    const cached = seoCache.get(cacheKey);
    if (isCacheValid(cached)) {
      return cached.data;
    }
    return getStaticSeoData(page, language);
  });
  const [isLoading, setIsLoading] = useState(() => {
    const cacheKey = `${page}_${language}`;
    return !isCacheValid(seoCache.get(cacheKey));
  });

  useEffect(() => {
    let mounted = true;
    const cacheKey = `${page}_${language}`;

    // Check cache first with TTL validation
    const cached = seoCache.get(cacheKey);
    if (isCacheValid(cached)) {
      if (mounted) {
        setSeoData(cached.data);
        setIsLoading(false);
      }
      return;
    }

    const fetchData = async () => {
      try {
        const { data, error } = await supabase
          .from('seo_settings')
          .select('title, description')
          .eq('page_key', page)
          .eq('language', language)
          .maybeSingle();

        if (mounted) {
          if (data && !error) {
            const seo = { title: data.title, description: data.description };
            seoCache.set(cacheKey, { data: seo, timestamp: Date.now() });
            setSeoData(seo);
          } else {
            // Use static data as fallback and cache it
            const staticData = getStaticSeoData(page, language);
            seoCache.set(cacheKey, { data: staticData, timestamp: Date.now() });
            setSeoData(staticData);
          }
        }
      } catch {
        if (mounted) {
          const staticData = getStaticSeoData(page, language);
          setSeoData(staticData);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [page, language]);

  return { seoData, isLoading };
}

// Clear cache (useful when CRM updates SEO settings)
export function clearSeoCache() {
  seoCache.clear();
}

// Prefetch SEO data for multiple pages at once (for navigation preloading)
export async function prefetchSeoData(pages: string[], language: string) {
  const uncachedPages = pages.filter(page => {
    const cacheKey = `${page}_${language}`;
    return !isCacheValid(seoCache.get(cacheKey));
  });

  if (uncachedPages.length === 0) return;

  try {
    const { data } = await supabase
      .from('seo_settings')
      .select('page_key, title, description')
      .eq('language', language)
      .in('page_key', uncachedPages);

    if (data) {
      const now = Date.now();
      data.forEach(item => {
        const cacheKey = `${item.page_key}_${language}`;
        seoCache.set(cacheKey, {
          data: { title: item.title, description: item.description },
          timestamp: now
        });
      });
    }
  } catch {
    // Silently fail - will use static data as fallback
  }
}
